import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

// RxJS
import { filter, map, Observable, take } from 'rxjs';

// SocketIO
import { io, Socket } from 'socket.io-client';

// Services
import { StateService } from '@modules/settings/services/state.service';

// Types
import { Team, User } from '@modules/users/types/user';
import { Project } from '@modules/project/types/project';
import { Record, RecordStatus, RecordTask, RecordTaskType } from '@modules/records/types/record';
import { Clip } from '@modules/preview/types/clip';
import { RecordEvent } from '@modules/records/types/record-event';

// Decorators
import { warmUpObservable } from '@decorators';

// Env
import { environment } from '@environment';
import { ServerTemplate } from '@modules/project/types/server-template';
import { TaskTemplate } from '@modules/project/types/task-template';
import { StreamSettings } from '@modules/preview/types/stream-settings';
import { SrtStats } from '@modules/preview/types/srt-stats';
import { C3Station } from '@modules/project/types/c3-station';
import { ApiClient } from '@modules/users/types/api-client';

@Injectable({
  providedIn: 'root'
})
export class NetworkService {

  // Private
  private server = `http${environment.ssl ? 's' : ''}://${environment.api}`;

  // Public
  public socket: Socket = io(`${this.server}/events`);

  /**
   * Constructor
   */

  constructor(
    private http: HttpClient,
    private stateService: StateService
  ) {
    this.stateService.getState()
      .pipe(
        filter(state => !!state.accessToken),
        map(state => state.accessToken),
        take(1)
      )
      .subscribe(accessToken => {
        const url = `${this.server}/events?token=${accessToken}`;
        (this.socket.io as any).uri = url;
        this.socket.disconnect().connect();
      });
  }

  /**
   * Auth
   */

  @warmUpObservable
  login(email: string, password: string): Observable<any> {
    return this.http.post(`${this.server}/auth/login`, {email, password});
  }

  @warmUpObservable
  invite(email: string, role: string, teamId: string): Observable<any> {
    return this.http.post(`${this.server}/auth/invite`, {email, role, teamId});
  }

  inviteValidate(token: string): Observable<any> {
    return this.http.get(`${this.server}/auth/invite/validate`, {params: {token}});
  }

  regenerateToken(userId: string): Observable<any> {
    return this.http.get(`${this.server}/auth/invite/regenerate/users/${userId}`);
  }

  resetPassword(userId: string): Observable<any> {
    return this.http.get(`${this.server}/auth/reset/password/${userId}`);
  }

  @warmUpObservable
  completeRegistration(email: string, password: string, name?: string): Observable<any> {
    const params: any = { password };
    if (name) {
      params['name'] = name;
    }
    return this.http.post(`${this.server}/auth/complete-registration?email=${email}`, params);
  }

  /**
   * Projects
   */

  @warmUpObservable
  createProject(name: string): Observable<any> {
    return this.http.post(`${this.server}/projects`, {name});
  }

  getProjects(): Observable<Project[]> {
    return this.http.get<Project[]>(`${this.server}/projects`);
  }

  getProject(id: string): Observable<Project> {
    return this.http.get<Project>(`${this.server}/projects/${id}`);
  }

  @warmUpObservable
  updateProject(id: string, project: Project): Observable<any> {
    return this.http.patch(`${this.server}/projects/${id}`, project);
  }

  addUserToProject(projectId: string, userId: string): Observable<any> {
    return this.http.post(`${this.server}/projects/${projectId}/user/${userId}`, {});
  }

  @warmUpObservable
  deleteProject(id: string): Observable<any> {
    return this.http.delete(`${this.server}/projects/${id}`);
  }

  /**
   * Server Templates
   */

  @warmUpObservable
  createServerTemplates(projectId: string, server: ServerTemplate): Observable<any> {
    return this.http.post<any>(`${this.server}/projects/${projectId}/server-templates`, server);
  }

  getServerTemplates(projectId: string): Observable<ServerTemplate[]> {
    return this.http.get<ServerTemplate[]>(`${this.server}/projects/${projectId}/server-templates`);
  }

  @warmUpObservable
  updateServerTemplates(projectId: string, server: ServerTemplate): Observable<any> {
    return this.http.patch<any>(`${this.server}/projects/${projectId}/server-templates/${server.id}`, server);
  }

  @warmUpObservable
  deleteServerTemplates(projectId: string, serverTemplateIds: string[]): Observable<any> {
    return this.http.delete<any>(`${this.server}/projects/${projectId}/server-templates/bulk`, {body: {serverTemplateIds }});
  }

  /**
   * Task Templates
   */

  @warmUpObservable
  createTaskTemplates(serverTemplateId: string, tasks: TaskTemplate[]): Observable<any> {
    return this.http.post<any>(`${this.server}/projects/server-templates/${serverTemplateId}/task-templates/bulk`, tasks);
  }

  @warmUpObservable
  updateTaskTemplates(serverTemplateId: string, tasks: TaskTemplate[]): Observable<any> {
    return this.http.patch<any>(`${this.server}/projects/server-templates/${serverTemplateId}/task-templates/bulk`, tasks);
  }

  /**
   * Users
   */

  getUsers(): Observable<User[]> {
    return this.http.get<User[]>(`${this.server}/users`);
  }

  @warmUpObservable
  updateUser(user: User): Observable<any> {
    return this.http.patch(`${this.server}/users/${user.id}`, user);
  }

  @warmUpObservable
  deleteUser(id: string): Observable<any> {
    return this.http.delete(`${this.server}/users/${id}`);
  }

  /**
   * Teams
   */

  @warmUpObservable
  createTeam(name: string, permissions: string[]): Observable<any> {
    return this.http.post(`${this.server}/teams`, {name, permissions});
  }

  @warmUpObservable
  updateTeam(team: Team): Observable<any> {
    return this.http.patch(`${this.server}/teams/${team.id}`, team);
  }

  @warmUpObservable
  deleteTeam(id: string): Observable<any> {
    return this.http.delete(`${this.server}/teams/${id}`);
  }

  getTeams(): Observable<Team[]> {
    return this.http.get<Team[]>(`${this.server}/teams`);
  }

  /**
   * Agent
   */

  @warmUpObservable
  refreshAgentData(recordId: string, serverId: string): Observable<any> {
    return this.http.post(`${this.server}/records/${recordId}/servers/${serverId}/refresh`, {});
  }

  @warmUpObservable
  restartAgent(recordId: string, serverId: string): Observable<any> {
    return this.http.post(`${this.server}/records/${recordId}/servers/${serverId}/restart`, {});
  }

  /**
   * Records
   */

  @warmUpObservable
  createRecord(record: Record): Observable<Record> {
    return this.http.post(`${this.server}/records`, record);
  }

  getRecords(projectId: string): Observable<Record[]> {
    return this.http.get<Record[]>(`${this.server}/records`, {params: {projectId}});
  }

  getRecord(id: string): Observable<Record> {
    return this.http.get<Record>(`${this.server}/records/${id}`);
  }

  @warmUpObservable
  updateRecord(record: Record): Observable<any> {
    return this.http.patch(`${this.server}/records/${record.id}`, record);
  }

  @warmUpObservable
  updateRecordStatus(recordId: string, status: RecordStatus): Observable<any> {
    return this.http.patch(`${this.server}/records/${recordId}/status`, { status });
  }

  @warmUpObservable
  startRecord(recordId: string, serverIds: string[], recordBody?: any): Observable<any> {
    const params: {[key: string]: any} = { serverIds };
    if (recordBody) {
      params['recordBody'] = recordBody;
    }
    return this.http.post(`${this.server}/records/${recordId}/actions/start`, params);
  }

  @warmUpObservable
  stopRecord(recordId: string, serverIds: string[]): Observable<any> {
    return this.http.post(`${this.server}/records/${recordId}/actions/stop`, { serverIds });
  }

  @warmUpObservable
  resetRecord(recordId: string, serverIds: string[]): Observable<any> {
    return this.http.post(`${this.server}/records/${recordId}/actions/reset`, { serverIds });
  }

  @warmUpObservable
  getEvents(recordId: string): Observable<RecordEvent[]> {
    return this.http.get<RecordEvent[]>(`${this.server}/records/${recordId}/events`, {});
  }

  @warmUpObservable
  cancelRecord(recordId: string): Observable<any> {
    return this.http.post(`${this.server}/records/${recordId}/cancel`, {});
  }

  @warmUpObservable
  restoreRecord(recordId: string): Observable<any> {
    return this.http.post(`${this.server}/records/${recordId}/restore`, {});
  }

  @warmUpObservable
  finishRecord(recordId: string, force: boolean): Observable<any> {
    return this.http.post(`${this.server}/records/${recordId}/finish`, {}, { params: { force }});
  }

  @warmUpObservable
  deleteRecords(ids: string[]): Observable<any> {
    return this.http.delete(`${this.server}/records/bulk`, {body: ids});
  }

  @warmUpObservable
  createTasks(recordId: string, serverId: string): Observable<any> {
    return this.http.post(`${this.server}/records/${recordId}/servers/${serverId}/tasks/create`, {});
  }

  @warmUpObservable
  resetRecordTasks(recordId: string, programs: ('main' | 'overtime')[]): Observable<any> {
    return this.http.post(`${this.server}/records/${recordId}/reset`, { programs });
  }

  @warmUpObservable
  recreateServerInstance(recordId: string, serverId: string): Observable<any> {
    return this.http.post(`${this.server}/records/${recordId}/servers/${serverId}/aws/recreate-instance`, {});
  }

  /**
   * Tasks
   */

  @warmUpObservable
  trim(recordId: string, serverIds: string[], start: string, end: string, keys: RecordTaskType[]): Observable<any> {
    return this.http.post(`${this.server}/records/${recordId}/trim`, { start, end, keys, serverIds });
  }

  @warmUpObservable
  startManualTask(recordId: string, serverIds: string[], key: string): Observable<any> {
    return this.http.post(`${this.server}/records/${recordId}/start-manual-task`, { serverIds, key });
  }

  @warmUpObservable
  restartTasks(recordId: string, serverIds: string[], taskId: string, restartOnlyThis: boolean = false): Observable<any> {
    return this.http.patch(`${this.server}/records/${recordId}/tasks/${taskId}/restart`, { serverIds, restartOnlyThis });
  }

  @warmUpObservable
  resetTask(recordId: string, serverIds: string[], taskId: string): Observable<any> {
    return this.http.patch(`${this.server}/records/${recordId}/tasks/${taskId}/reset`, { serverIds });
  }

  @warmUpObservable
  updateTask(serverId: string, task: RecordTask): Observable<any> {
    return this.http.patch(`${this.server}/records/servers/${serverId}/tasks/${task.id}`, task);
  }

  @warmUpObservable
  forceDoneTask(recordId: string, serverIds: string[], key: string): Observable<any> {
    return this.http.post(`${this.server}/records/${recordId}/tasks/force-done`, { serverIds, key });
  }

  @warmUpObservable
  s3Download(recordId: string, serverIds: string[], s3VideoUrl: string): Observable<any> {
    return this.http.post(`${this.server}/records/${recordId}/download-s3`, { serverIds, s3VideoUrl });
  }

  @warmUpObservable
  startICap(recordId: string, serverIds: string[], start: string, end: string): Observable<any> {
    return this.http.post(`${this.server}/records/${recordId}/start-icap`, { serverIds, start, end });
  }

  /**
   * Cinedeck
   */

  @warmUpObservable
  playback(recordId: string, action: string, body: any = {}): Observable<any> {
    return this.http.post(`${this.server}/records/${recordId}/cinedeck/${action}`, body);
  }

  getIndexedFiles(recordId: string, body: any = {}): Observable<Clip[]> {
    return this.http.post<Clip[]>(`${this.server}/records/${recordId}/cinedeck/indexed_files`, body);
  }

  getClip(recordId: string, serverIds: string[], path: string): Observable<Clip | undefined> {
    return this.http.post<{data: Clip}>(`${this.server}/records/${recordId}/cinedeck/clip`, { path, serverIds })
      .pipe(
        map(response => response?.data)
      );
  }

  getSettings(recordId: string, serverIds: string[], channel: number): Observable<StreamSettings | undefined> {
    return this.http.post<{data: StreamSettings}>(`${this.server}/records/${recordId}/cinedeck/settings`, { channel, serverIds })
      .pipe(
        map(response => response?.data)
      );
  }

  getSrtStats(recordId: string, serverId: string): Observable<SrtStats> {
    return this.http.get<SrtStats>(`${this.server}/records/${recordId}/cinedeck/srt-stats`, { params: { serverId } });
  }

  /**
   * Cinedeck Server Admin
   */

  getVersions(type: 'agent' | 'cinedeck'): Observable<any> {
    return this.http.get<any>(`${this.server}/versions`, {params: { type } });
  }

  @warmUpObservable
  updateVersions(type: 'agent' | 'cinedeck', version: string): Observable<any> {
    return this.http.post<any>(`${this.server}/versions`, { type, version });
  }

  @warmUpObservable
  uploadCinedeckServer(file: any): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<any>(`${this.server}/versions/upload`, formData);
  }

  /**
   * API Keys
   */

  @warmUpObservable
  createApiKey(key: ApiClient): Observable<any> {
    return this.http.post(`${this.server}/api-clients`, key);
  }

  getApiKeys(): Observable<ApiClient[]> {
    return this.http.get<ApiClient[]>(`${this.server}/api-clients`);
  }

  getApiKey(id: string): Observable<ApiClient> {
    return this.http.get<ApiClient>(`${this.server}/api-clients/${id}`);
  }

  updateApiKey(key: ApiClient): Observable<any> {
    return this.http.patch(`${this.server}/api-clients/${key.id}`, key);
  }

  deleteApiKey(id: string): Observable<any> {
    return this.http.delete(`${this.server}/api-clients/${id}`);
  }

  revokeApiKey(id: string): Observable<any> {
    return this.http.post(`${this.server}/api-clients/${id}/revoke`, {});
  }

  rotateApiKey(id: string): Observable<any> {
    return this.http.post(`${this.server}/api-clients/${id}/rotate`, {});
  }

  /**
   * C3 Stations
   */

  @warmUpObservable
  createStations(stations: {stationName: string, stationId: string}[]): Observable<any> {
    return this.http.post(`${this.server}/c3-stations`, { stations });
  }

  getStations(): Observable<C3Station[]> {
    return this.http.get<C3Station[]>(`${this.server}/c3-stations`);
  }

  getStationByName(stationName: string): Observable<C3Station> {
    return this.http.get<C3Station>(`${this.server}/c3-stations/${stationName}`);
  }

  @warmUpObservable
  updateStation(station: C3Station): Observable<C3Station> {
    return this.http.put<C3Station>(`${this.server}/c3-stations/${station.id}`, { stationName: station.stationName, stationId: station.stationId });
  }

  @warmUpObservable
  deleteStation(id: string): Observable<any> {
    return this.http.delete(`${this.server}/c3-stations/${id}`);
  }

}
